// Read-only records table (architecture sprint-05 §6.2): every header toggles a server-side sort.
// No actions column and no row click: Admins never edit others' entries.
import { ArrowDown, ArrowUp } from 'lucide-react';
import { ColorSwatch } from '@/components/ColorSwatch';
import { ShiftBadge } from '@/components/shifts/ShiftBadge';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { diffDays } from '@shared/calendar';
import { formatClock, formatHours2, formatLocalDate } from '@/lib/format';
import type { RecordRow, RecordSort, SortDirection } from '@/types/admin';

const COLUMNS: Array<{ key: RecordSort; label: string; align?: 'right' }> = [
  { key: 'date', label: 'Date' },
  { key: 'user', label: 'User' },
  { key: 'project', label: 'Project' },
  { key: 'description', label: 'Description' },
  { key: 'start', label: 'Start' },
  { key: 'end', label: 'End' },
  { key: 'hours', label: 'Hours', align: 'right' },
];

interface RecordsTableProps {
  rows: RecordRow[];
  sort: RecordSort;
  dir: SortDirection;
  onSort: (column: RecordSort) => void;
}

function ariaSort(active: boolean, dir: SortDirection): 'ascending' | 'descending' | 'none' {
  if (!active) return 'none';
  return dir === 'asc' ? 'ascending' : 'descending';
}

export function RecordsTable({ rows, sort, dir, onSort }: RecordsTableProps): JSX.Element {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {COLUMNS.map((c) => {
            const active = c.key === sort;
            return (
              <TableHead key={c.key} aria-sort={ariaSort(active, dir)} className={c.align === 'right' ? 'text-right' : undefined}>
                <button type="button" className="inline-flex items-center gap-1 hover:text-foreground" onClick={() => onSort(c.key)}>
                  {c.label}
                  {active && (dir === 'asc' ? <ArrowUp aria-hidden="true" className="size-3" /> : <ArrowDown aria-hidden="true" className="size-3" />)}
                </button>
              </TableHead>
            );
          })}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => {
          const extraDays = diffDays(r.endDate, r.date);
          return (
            <TableRow key={r.id}>
              <TableCell className="whitespace-nowrap">{formatLocalDate(r.date)}</TableCell>
              <TableCell className="whitespace-nowrap">
                {r.user.name}
                {r.user.status === 'deactivated' && <span className="text-muted-foreground"> (deactivated)</span>}
              </TableCell>
              <TableCell className="whitespace-nowrap">
                {r.project ? (
                  <span className="inline-flex items-center gap-1.5">
                    <ColorSwatch hex={r.project.color} />
                    {r.project.name}
                    {r.project.status === 'archived' && <span className="text-muted-foreground"> (archived)</span>}
                  </span>
                ) : (
                  <span className="text-muted-foreground">No project</span>
                )}
              </TableCell>
              <TableCell className="max-w-xs truncate" title={r.description ?? undefined}>
                {r.isShift ? <ShiftBadge /> : (r.description ?? '')}
              </TableCell>
              <TableCell className="font-mono tabular-nums">{formatClock(r.startSecond)}</TableCell>
              <TableCell className="whitespace-nowrap font-mono tabular-nums">
                {formatClock(r.endSecond)}
                {extraDays > 0 && (
                  <Badge variant="info" className="ml-1.5 font-sans">
                    +{extraDays} day
                  </Badge>
                )}
              </TableCell>
              <TableCell className="text-right font-mono tabular-nums">{formatHours2(r.durationSeconds)}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
